import { useState, useMemo, useEffect } from "react";
import { createPortal } from "react-dom";
import { EthernetPort, Network } from "lucide-react";
import baseapi from "../../../../services/baseapi";
import { showError } from "../../../../utils/toast";

export interface InterfaceEntry {
    id: number;
    created: string;
    updated: string;
    is_deleted: boolean;
    agent_id: number;
    client_id: number;
    organization_id: number;
    asset_id: number;
    interface_index: number;
    name: string;
    mac_address: string;
    admin_status: string;
    oper_status: string;
    speed_mbps: number;
}

interface NvrPortVisualizationProps {
    assetId: number;
    clientId: number;
    onDataFetched?: (data: InterfaceEntry[]) => void;
    initialData?: InterfaceEntry[];
}

export default function NvrPortVisualization({ assetId, clientId, onDataFetched, initialData = [] }: NvrPortVisualizationProps) {
    const [data, setData] = useState<InterfaceEntry[]>(initialData);
    const [loading, setLoading] = useState(initialData.length === 0);
    const [tooltipInfo, setTooltipInfo] = useState<{
        port: InterfaceEntry;
        x: number;
        y: number;
        isTopRow: boolean;
    } | null>(null);

    const fetchInterfaceData = async () => {
        if (!assetId) return;

        setLoading(true);
        try {
            const params = {
                query: JSON.stringify({ asset_id: assetId, client_id: clientId }),
                skip: 0,
                limit: 100
            };

            const response = await baseapi.get("/nvr_interfaces", { params });

            if (response.data.status) {
                const sortedData = [...response.data.data].sort((a, b) => a.interface_index - b.interface_index);
                setData(sortedData);
                if (onDataFetched) onDataFetched(sortedData);
            } else {
                showError(response.data.message || "Failed to load interfaces");
            }
        } catch (error: any) {
            showError(error.message || "An error occurred while fetching interfaces");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (initialData.length > 0) {
            setData(initialData);
            setLoading(false);
        } else {
            fetchInterfaceData();
        }
    }, [assetId, initialData]);


    // Determine layout mapping
    const portMapping = useMemo(() => {
        const needsTwoRows = data.length > 10;

        let portCount = needsTwoRows ? Math.max(data.length, 12) : Math.max(data.length, 6);
        if (needsTwoRows && portCount % 2 !== 0) portCount += 1;

        const slots: (InterfaceEntry | null)[] = Array(portCount).fill(null);

        // Map interfaces into slots based on their interface_index
        data.forEach((port) => {
            const index = port.interface_index > 0 ? port.interface_index - 1 : -1;
            if (index >= 0 && index < portCount) {
                slots[index] = port;
            } else {
                const emptySlot = slots.findIndex(s => s === null);
                if (emptySlot !== -1) slots[emptySlot] = port;
            }
        });

        const columns: (InterfaceEntry | null)[][] = [];

        if (needsTwoRows) {
            // Two rows: Column mapping (Top = Odd index, Bottom = Even index)
            for (let i = 0; i < portCount; i += 2) {
                columns.push([slots[i], slots[i + 1]]);
            }
        } else {
            // One row: Just push single items into columns
            for (let i = 0; i < portCount; i++) {
                columns.push([slots[i]]);
            }
        }

        return { columns, needsTwoRows };
    }, [data]);

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center p-8 bg-gray-800/20 border border-gray-700/50 rounded-lg">
                <div className="w-5 h-5 border-2 border-indigo-500/20 border-t-indigo-500 rounded-full animate-spin"></div>
                <p className="text-[9px] font-black text-gray-500 uppercase tracking-[0.2em] mt-2">Loading Visualization...</p>
            </div>
        );
    }

    if (data.length === 0) return null;

    return (
        <div className="w-full flex flex-col gap-1 text-gray-200">
            {/* Header */}
            <div className="flex items-center justify-between px-1">
                <div className="flex items-center gap-2">
                    <div className="bg-indigo-500/10 p-1.5 rounded-md border border-indigo-500/20">
                        <Network size={14} className="text-indigo-400" />
                    </div>
                    <div>
                        <h2 className="text-[11px] font-bold text-white tracking-wide leading-tight">Port Status</h2>
                        <p className="text-[8px] text-gray-400 uppercase tracking-widest mt-0.5">Hardware Visualization</p>
                    </div>
                </div>
            </div>

            <div
                className="overflow-x-auto pb-2 rounded-xl p-1"
                onScroll={() => setTooltipInfo(null)}
            >
                <div className="flex justify-center min-w-max">
                    <div className="border border-slate-700/80 bg-[#161a23] rounded-2xl p-4 shadow-2xl">
                        <div className="flex gap-2">
                            {portMapping.columns.map((col, colIdx) => (
                                <div key={colIdx} className="flex flex-col gap-3">
                                    {col.map((port, rowIdx) => {
                                        const isTopRow = portMapping.needsTwoRows ? rowIdx === 0 : true;
                                        const isUp = port && port.oper_status.toLowerCase() === 'up';

                                        const portNumber = portMapping.needsTwoRows
                                            ? (colIdx * 2 + rowIdx + 1)
                                            : (colIdx + 1);

                                        return (
                                            <div
                                                key={port ? port.id : `empty-${portNumber}`}
                                                onMouseEnter={(e) => {
                                                    if (port) {
                                                        const rect = e.currentTarget.getBoundingClientRect();
                                                        setTooltipInfo({
                                                            port,
                                                            x: rect.left + rect.width / 2,
                                                            y: isTopRow ? rect.top : rect.bottom,
                                                            isTopRow
                                                        });
                                                    }
                                                }}
                                                onMouseLeave={() => setTooltipInfo(null)}
                                                className="flex flex-col items-center"
                                            >
                                                {isTopRow && (
                                                    <span className="text-[10px] font-bold text-white mb-2 leading-none">
                                                        {portNumber}
                                                    </span>
                                                )}

                                                <div className={`
                                                    w-10 h-10 flex items-center justify-center rounded-lg border transition-all duration-200 relative
                                                    ${port
                                                        ? 'cursor-pointer'
                                                        : 'cursor-default opacity-30'}
                                                    ${isUp
                                                        ? 'bg-[#1a2322] border-emerald-500 shadow-[0_0_12px_rgba(16,185,129,0.15)]'
                                                        : 'bg-[#181a20] border-slate-700'}
                                                `}>
                                                    {port && (
                                                        <div className={`
                                                            absolute top-1 w-1 h-1 rounded-full 
                                                            ${isUp ? 'bg-emerald-400 shadow-[0_0_4px_#34d399]' : 'bg-slate-600'}
                                                            left-1
                                                        `} />
                                                    )}

                                                    <EthernetPort size={20} className={`
                                                        ${!isTopRow && portMapping.needsTwoRows ? 'rotate-180' : ''} 
                                                        ${isUp ? 'text-emerald-400' : 'text-slate-500'}
                                                    `} />
                                                </div>

                                                {!isTopRow && portMapping.needsTwoRows && (
                                                    <span className="text-[10px] font-bold text-white mt-2 leading-none">
                                                        {portNumber}
                                                    </span>
                                                )}
                                            </div>
                                        );
                                    })}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </div>

            {tooltipInfo && typeof document !== 'undefined' && createPortal(
                <div
                    className="fixed z-[9999] pointer-events-none animate-in fade-in duration-200"
                    style={{
                        left: tooltipInfo.x,
                        top: tooltipInfo.isTopRow ? tooltipInfo.y - 8 : tooltipInfo.y + 8,
                        transform: `translate(-50%, ${tooltipInfo.isTopRow ? '-100%' : '0'})`
                    }}
                >
                    <div className="bg-slate-800 border border-slate-700 text-white rounded-lg shadow-xl p-2.5 w-40 flex flex-col gap-1.5">
                        <div className="flex justify-between items-center mb-1 border-b border-slate-700 pb-1.5">
                            <span className="text-[11px] font-bold text-white">{tooltipInfo.port.name}</span>
                            <div className={`w-1.5 h-1.5 rounded-full ${tooltipInfo.port.oper_status.toLowerCase() === 'up' ? 'bg-emerald-500 shadow-[0_0_4px_#10b981]' : 'bg-rose-500'}`} />
                        </div>

                        <div className="flex justify-between items-center text-[9px]">
                            <span className="text-gray-200">Status:</span>
                            <span className={tooltipInfo.port.oper_status.toLowerCase() === 'up' ? 'text-emerald-400 font-bold' : 'text-rose-400 font-bold'}>
                                {tooltipInfo.port.oper_status}
                            </span>
                        </div>

                        <div className="flex justify-between items-center text-[9px]">
                            <span className="text-gray-200">Speed:</span>
                            <span className="text-gray-200">{tooltipInfo.port.speed_mbps} Mbps</span>
                        </div>
                    </div>
                </div>,
                document.body
            )}
        </div>
    );
}
